import { useEffect, useMemo, useState } from "react";
import { Button, Form, InputNumber, Modal, Radio, Select, Space } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { handleAllowNumbersOnly } from "../../helpers/helpfulFunctions";
import {
  getPaymentTypesApi,
  getWalletInfoForUser,
  placeBuyAndSupplyOrder,
} from "../../network";
import { toastError, toastSuccess } from "../../helpers/toasters";
import { formatPrice } from "../../helpers/formattedPrice";
import { ROUTES } from "../../constants/_routes";

import dropdownArrowIcon from "../../assets/icons/blue-dropdown-arrow.svg";
import closeIcon from "../../assets/icons/buy-and-supply-close-button.svg";
import BankTransfer from "../Common/BanckTransfer";
import CombinedInvoice from "./CombinedInvoice";

const CombinedModal = ({ open, onCancel, selectedItem, onSubmit }) => {
  const [orderTypes, setOrderTypes] = useState(null);
  const [selectedDayId, setSelectedDayId] = useState(null);
  const [submittable, setSubmittable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1 form, 2 bank, 3 invoice
  const [payload, setPayload] = useState(null);
  const [invoiceTotals, setInvoiceTotals] = useState(null);
  const { dir } = useSelector((state) => state.language);
  const { authorization } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const currency = authorization?.isEmirate
    ? t("buyAndSupply.aed")
    : t("buyAndSupply.egp");

  const durationOptions = useMemo(
    () => selectedItem?.pricePerKiloList || [],
    [selectedItem],
  );
  const hasDurationOptions = durationOptions.length > 0;

  const quantity = Number(values?.quantity) || 0;
  const totalPurchasePrice = quantity * (selectedItem?.pricePerKilo || 0);
  const walletPounds = Number(walletData?.pounds) || 0;
  const walletCoversFully =
    walletPounds > 0 &&
    walletPounds >= totalPurchasePrice &&
    totalPurchasePrice > 0;
  const walletPartial = walletPounds > 0 && walletPounds < totalPurchasePrice;
  const showWalletRows = walletCoversFully || walletPartial;

  const walletDeduction = walletCoversFully
    ? totalPurchasePrice
    : walletPartial
      ? walletPounds
      : 0;
  const priceAfterWallet = Math.max(totalPurchasePrice - walletDeduction, 0);

  const selectedDuration = durationOptions.find(
    (opt) => opt.id === selectedDayId,
  );
  const supplyPricePerKilo = selectedDuration?.price ?? 0;
  const receivingAmount = quantity * supplyPricePerKilo;
  const managementFeesPercent = Number(selectedItem?.managementFees) || 0;
  const managementFee = (receivingAmount * managementFeesPercent) / 100;
  const finalAmount = Math.max(receivingAmount - managementFee, 0);

  const isBankPayment = payload?.paymentTypeId === 9;

  const validateQuantity = (_, value) => {
    if (!value && value !== 0) {
      return Promise.reject(t("buyAndSupply2026.requiredQuantityMsg"));
    }
    if (isNaN(value) || value <= 0) {
      return Promise.reject(t("buyAndSupply2026.invalidQuantityMsg"));
    }
    if (value > selectedItem.quantityPerKilo) {
      return Promise.reject(
        t("buyAndSupply2026.exceedingAvailableQuantityMsg"),
      );
    }
    return Promise.resolve();
  };

  const handleNextStep = (formValues) => {
    const paymentTypeId = walletCoversFully ? 0 : formValues.paymentMethod;
    const paymentTypeName = walletCoversFully
      ? t("buyAndSupply2026.walletPayment")
      : orderTypes?.find((ele) => ele.value === formValues.paymentMethod)
          ?.label;

    const nextPayload = {
      itemId: selectedItem.itemId,
      quantity: formValues.quantity,
      paymentTypeId,
      paymentTypeName,
      pricePerKiloListId: formValues.duration,
      allPaymentFromWallet: walletCoversFully,
    };

    setPayload(nextPayload);
    setInvoiceTotals({
      totalPurchasePrice,
      walletDeduction,
      priceAfterWallet,
      receivingAmount,
      managementFeesPercent,
      managementFee,
      finalAmount,
      showWalletRows,
    });

    // Bank (9) → bank details first; cash / wallet / other → invoice
    setCurrentStep(paymentTypeId === 9 ? 2 : 3);
  };

  const handlePlaceOrder = () => {
    if (isSubmitting || !payload) return;
    setIsSubmitting(true);

    const apiPayload = {
      itemId: payload.itemId,
      quantity: payload.quantity,
      paymentTypeId: payload.paymentTypeId,
      pricePerKiloListId: payload.pricePerKiloListId,
      allPaymentFromWallet: payload.allPaymentFromWallet,
    };

    placeBuyAndSupplyOrder(
      apiPayload,
      (res) => {
        if (res.success) {
          if (payload.paymentTypeId === 2) {
            navigate(ROUTES.PAYMOB, {
              state: { paymentURL: res.data.paymentURL },
            });
          } else if (payload.paymentTypeId === 7) {
            window.location.href = res.data.paymentURL;
          } else {
            setIsSubmitting(false);
            toastSuccess(res.message);
            onSubmit?.();
          }
        } else {
          setIsSubmitting(false);
          toastError(res.message);
        }
      },
      (res) => {
        setIsSubmitting(false);
        toastError(res.message);
      },
    );
  };

  const handleInvoiceBack = () => {
    setCurrentStep(isBankPayment ? 2 : 1);
  };

  useEffect(() => {
    if (walletCoversFully) {
      form.setFieldsValue({ paymentMethod: undefined });
    }
  }, [walletCoversFully, form]);

  useEffect(() => {
    if (currentStep !== 1) return;
    form.validateFields({ validateOnly: true }).then(
      () => setSubmittable(true),
      (info) => {
        setSubmittable(info.errorFields.length === 0);
      },
    );
  }, [values, form, selectedDayId, walletCoversFully, currentStep]);

  useEffect(() => {
    getPaymentTypesApi(
      (res) => {
        if (res.success) {
          setOrderTypes(
            res.data.map((ele) => ({
              value: ele.id,
              label: ele.name,
            })),
          );
        } else {
          toastError(res.message);
        }
      },
      (res) => toastError(res.message),
      selectedItem?.itemId,
    );
    getWalletInfoForUser(
      (res) => {
        if (res.success) {
          setWalletData(res.data);
        } else {
          toastError(res.message);
        }
      },
      (res) => toastError(res.message),
    );
  }, [selectedItem?.itemId]);

  return (
    <Modal
      centered
      destroyOnClose={true}
      open={open}
      footer={false}
      closable={true}
      maskClosable={false}
      closeIcon={<img src={closeIcon} alt="" className="d-block img-fluid" />}
      width={
        window.innerWidth >= 1440
          ? "70%"
          : window.innerWidth >= 992
            ? "55%"
            : window.innerWidth >= 768
              ? "75%"
              : "100%"
      }
      className="buy-and-supply-2026__modal"
      onCancel={onCancel}
    >
      <div dir={dir} className="buy-and-supply-2026__modal__body">
        {currentStep === 2 && (
          <BankTransfer
            itemId={selectedItem?.itemId}
            onBack={() => setCurrentStep(1)}
            onContinue={() => setCurrentStep(3)}
          />
        )}

        {currentStep === 3 && payload && invoiceTotals && (
          <CombinedInvoice
            item={selectedItem}
            payload={payload}
            totalPurchasePrice={invoiceTotals.totalPurchasePrice}
            walletDeduction={invoiceTotals.walletDeduction}
            priceAfterWallet={invoiceTotals.priceAfterWallet}
            receivingAmount={invoiceTotals.receivingAmount}
            managementFeesPercent={invoiceTotals.managementFeesPercent}
            managementFee={invoiceTotals.managementFee}
            finalAmount={invoiceTotals.finalAmount}
            showWalletRows={invoiceTotals.showWalletRows}
            currency={currency}
            isSubmitting={isSubmitting}
            onBack={handleInvoiceBack}
            onSubmit={handlePlaceOrder}
          />
        )}

        {currentStep === 1 && (
          <>
            <h3 className="buy-and-supply-2026__modal__title text-center main-dark-text fw-bold mb-3 mb-md-4">
              {t("buyAndSupply2026.fillDataTitle")}
            </h3>

            <Form
              dir={dir}
              form={form}
              onFinish={handleNextStep}
              initialValues={{
                quantity: undefined,
                paymentMethod: undefined,
                duration: undefined,
              }}
            >
              <div className="buy-and-supply-2026__modal__section-holder">
                {/* buy section */}
                <div className="buy-and-supply-2026__modal__section-holder-wrapper">
                  <h4 className="buy-and-supply-2026__modal__section-title fw-bold mb-3">
                    {t("buyAndSupply2026.buySection")}
                  </h4>
                  <section className="buy-and-supply-2026__modal__section mb-3">
                    <p className="buy-and-supply-2026__modal__field-label text--secondary-8 fw-bold mb-1">
                      {t("buyAndSupply2026.quantity")}
                    </p>
                    <Form.Item
                      name="quantity"
                      rules={[{ validator: validateQuantity }]}
                      className="mb-3"
                    >
                      <InputNumber
                        keyboard={false}
                        controls={false}
                        placeholder={"X"}
                        addonAfter={
                          <span className="fw-bold fsize-5 text--secondary-8">
                            {t("buyAndSupply2026.kg")}
                          </span>
                        }
                        className="auction-requests__accept-reject-modal__input-holder__input d-block w-100 fsize-8 fw-bold"
                        onKeyDown={(e) => handleAllowNumbersOnly(e, true)}
                      />
                    </Form.Item>

                    {!walletCoversFully && (
                      <>
                        <p className="buy-and-supply-2026__modal__field-label text--secondary-8 fw-bold mb-1">
                          {t("buyAndSupply2026.paymentMethod")}
                        </p>
                        <Form.Item
                          name="paymentMethod"
                          rules={[
                            {
                              required: true,
                              message: t(
                                "buyAndSupply2026.requiredPaymentMethod",
                              ),
                            },
                          ]}
                          className="mb-3"
                        >
                          <Select
                            className="buy-and-supply__buy-modal__select fsize-5 fw-bold"
                            placeholder={t(
                              "buyAndSupply2026.paymentMethodPlaceholder",
                            )}
                            loading={!orderTypes}
                            suffixIcon={
                              <img
                                src={dropdownArrowIcon}
                                alt=""
                                className="d-block img-fluid"
                              />
                            }
                            getPopupContainer={(trigger) =>
                              trigger.parentElement
                            }
                          >
                            {orderTypes?.map((type) => (
                              <Select.Option
                                className={`buy-and-supply__buy-modal__select__option fsize-9 ${
                                  dir === "rtl" ? "text-end" : "text-start"
                                }`}
                                value={type.value}
                                key={type.value}
                              >
                                {type.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </>
                    )}

                    <div className="buy-and-supply-2026__modal__summary buy-and-supply-2026__modal__summary--buy fw-500 d-flex flex-column gap-1">
                      <div className="d-flex justify-content-between gap-2">
                        <span>{t("buyAndSupply2026.totalPurchasePrice")}</span>
                        <span>
                          {formatPrice?.(totalPurchasePrice)} {currency}
                        </span>
                      </div>
                      {showWalletRows && (
                        <>
                          <div className="d-flex justify-content-between gap-2">
                            <span>{t("buyAndSupply2026.walletDeduction")}</span>
                            <span>
                              {formatPrice?.(walletDeduction)} {currency}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between gap-2">
                            <span>
                              {t("buyAndSupply2026.priceAfterWalletDeduction")}
                            </span>
                            <span>
                              {formatPrice?.(priceAfterWallet)} {currency}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </section>
                </div>
                {/* supply section */}
                <div className="buy-and-supply-2026__modal__section-holder-wrapper">
                  <h4 className="buy-and-supply-2026__modal__section-title fw-bold mb-3">
                    {t("buyAndSupply2026.supplySection")}
                  </h4>
                  <section className="buy-and-supply-2026__modal__section mb-3">
                    {hasDurationOptions ? (
                      <>
                        <p className="buy-and-supply-2026__modal__duration-label fw-500 mb-3">
                          {t("buyAndSupply2026.selectDuration")}{" "}
                          <span className="text-danger">*</span>
                        </p>

                        <Form.Item
                          name="duration"
                          rules={[
                            {
                              required: true,
                              message: t("buyAndSupply2026.requiredDuration"),
                            },
                          ]}
                          className="mb-3"
                        >
                          <Radio.Group
                            name="duration"
                            onChange={(e) => setSelectedDayId(e.target.value)}
                            style={{ width: "100%" }}
                          >
                            <Space
                              direction="vertical"
                              className="buy-and-supply__supply-modal__radio-holder buy-and-supply-2026__modal__radios"
                            >
                              {durationOptions.map((option) => (
                                <Radio
                                  value={option.id}
                                  key={option.id}
                                  className="buy-and-supply-2026__modal__radio"
                                >
                                  <span className="buy-and-supply-2026__modal__radio-content">
                                    <span className="buy-and-supply-2026__modal__radio-title main-dark-text fw-bold">
                                      {option.daysMessage}
                                    </span>
                                    {option.priceMessage ? (
                                      <span className="buy-and-supply-2026__modal__radio-subtitle">
                                        {option.priceMessage}
                                      </span>
                                    ) : null}
                                  </span>
                                </Radio>
                              ))}
                            </Space>
                          </Radio.Group>
                        </Form.Item>
                      </>
                    ) : (
                      <p className="buy-and-supply-2026__modal__duration-label fw-500 mb-3">
                        {t("buyAndSupply2026.durationPlaceholder")}
                      </p>
                    )}
                  </section>
                </div>
              </div>

              <section className="buy-and-supply-2026__modal__summary-section">
                <div className="buy-and-supply-2026__modal__summary-section-body d-flex flex-column gap-1">
                  <div className="d-flex justify-content-between gap-2 buy-and-supply-2026__modal__summary-section-line fw-500">
                    <span>
                      {t("buyAndSupply2026.totalPurchasePrice")}{" "}
                      <span className="buy-and-supply-2026__modal__summary-section-note">
                        {t("buyAndSupply2026.youPayNow")}
                      </span>
                    </span>
                    <span>
                      {formatPrice?.(priceAfterWallet)} {currency}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between gap-2 buy-and-supply-2026__modal__summary-section-line fw-500">
                    <span>
                      {t("buyAndSupply2026.receivingAmount")}{" "}
                      <span className="buy-and-supply-2026__modal__summary-section-note">
                        {t("buyAndSupply2026.receivingAmountPeriod")}
                      </span>
                    </span>
                    <span>
                      {formatPrice?.(receivingAmount)} {currency}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between gap-2 buy-and-supply-2026__modal__summary-section-fee fw-500">
                    <span>
                      *{t("buyAndSupply2026.managementFee")} (
                      {managementFeesPercent}%):
                    </span>
                    <span>
                      {formatPrice?.(managementFee)} {currency}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between gap-2 buy-and-supply-2026__modal__summary-section-final fw-bold">
                    <span>{t("buyAndSupply2026.finalAmount")}</span>
                    <span>
                      {formatPrice?.(finalAmount)} {currency}
                    </span>
                  </div>
                </div>
              </section>

              <Button
                className="auction-requests__accept-reject-modal__confirm-btn w-100 fsize-9 fw-bold h-auto mx-auto d-block text-white py-2"
                htmlType="submit"
                disabled={!submittable || !hasDurationOptions}
              >
                {t("buyAndSupply2026.next")}
              </Button>
            </Form>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CombinedModal;
